import { ImageMagnifier } from '@/components/ImageMagnifier'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  ReportProblem as ReportProblemIcon,
  RotateLeft as RotateLeftIcon,
  RotateRight as RotateRightIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { useIsMobile } from 'hooks/screen/useIsMobile'
import { DamageReport, DamageReportItem } from '@otw/models'
import { useVehicle } from 'hooks/inventory/useVehicle'
import { useDamageReports } from 'hooks/useDamageReports'
import { useEffect, useState } from 'react'

const formatCurrency = (value: number) =>
  `$${value.toLocaleString('en-US', { maximumFractionDigits: 0 })}`

const capitalize = (value: string) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : value)

interface PhotoData {
  id: string
  url: string
  angle: string
  vehicleInfo: string
  uploadedAt?: string
  vehicleId?: string
  mediaType?: 'photo' | 'video'
}

interface PhotoComparisonDialogProps {
  open: boolean
  onClose: () => void
  photos: PhotoData[]
  currentPhotoIndex: number
  onPhotoChange: (index: number) => void
}

const PhotoViewerDialog = ({
  open,
  onClose,
  photos,
  currentPhotoIndex,
  onPhotoChange,
}: PhotoComparisonDialogProps) => {
  const { showSuccess, showError } = useSnackbar()
  const currentPhoto = photos[currentPhotoIndex] || null
  const {
    damageReport,
    isLoading: isLoadingDamageReport,
    isInspectingPhoto,
    generateAndSaveDamageReport,
  } = useDamageReports(currentPhoto?.id)
  const isMobile = useIsMobile()
  const [showDamageReport, setShowDamageReport] = useState(false)
  // Track rotation angle for each photo by photo ID
  const [photoRotations, setPhotoRotations] = useState<Record<string, number>>({})

  useEffect(() => {
    if (damageReport && !showDamageReport) {
      setShowDamageReport(true)
    }
  }, [damageReport])

  const formatTimestamp = (timestamp: string) => {
    try {
      return new Date(timestamp).toLocaleString()
    } catch (error) {
      return 'Unknown time'
    }
  }

  const downloadPhoto = async (url: string, filename: string) => {
    try {
      const response = await fetch(url)
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      const blob = await response.blob()
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(downloadUrl)
    } catch (error) {
      console.error('Error downloading photo:', error)
    }
  }

  const handleDownload = () => {
    if (!currentPhoto) return
    
    // Always download as webp format
    const filename = `${currentPhoto.angle.replace(/\s+/g, '_')}_${Date.now()}.webp`
    downloadPhoto(currentPhoto.url, filename)
  }

  const handleInspectDamage = async () => {
    if (!currentPhoto) return
    try {
      await generateAndSaveDamageReport(currentPhoto.id, currentPhoto.url, currentPhoto.angle)
      setShowDamageReport(true)
    } catch (error) {
      console.error('Failed to inspect damage:', error)
    }
  }



  const renderDamageReport = (report: DamageReport) => {
    if (report.no_damage_detected) {
      return (
        <Box>
          <Box
            sx={{
              p: 2,
              mb: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: (theme) => alpha(theme.palette.success.main, 0.4),
              bgcolor: (theme) => alpha(theme.palette.success.main, 0.08),
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleIcon color='success' fontSize='small' />
              <Typography variant='subtitle2' sx={{ fontWeight: 700 }}>
                No damage detected
              </Typography>
            </Box>
          </Box>
          {report.overall_explanation && (
            <Typography variant='body2' color='text.secondary'>
              {report.overall_explanation}
            </Typography>
          )}
        </Box>
      )
    }

    const items = report.damage_report ?? []

    return (
      <Box>
        <Box
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 2,
            border: '1px solid',
            borderColor: (theme) => alpha(theme.palette.warning.main, 0.4),
            bgcolor: (theme) => alpha(theme.palette.warning.main, 0.08),
          }}
        >
          <Typography variant='overline' color='text.secondary' sx={{ lineHeight: 1.4 }}>
            Estimated repair cost
          </Typography>
          <Typography variant='h6' sx={{ fontWeight: 700 }}>
            {formatCurrency(report.total_cost_low)} – {formatCurrency(report.total_cost_high)}
          </Typography>
          <Chip
            size='small'
            color='error'
            variant='outlined'
            label={items.length === 1 ? '1 issue found' : `${items.length} issues found`}
            sx={{ mt: 1, bgcolor: 'background.paper' }}
          />
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {items.map((item: DamageReportItem, index: number) => (
            <Box
              key={`${item.part}-${index}`}
              sx={{
                p: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant='subtitle2' sx={{ fontWeight: 700, flex: 1 }}>
                  {item.part}
                </Typography>
                <Chip
                  label={capitalize(item.severity)}
                  size='small'
                  color={
                    item.severity === 'minor' ? 'success' : item.severity === 'moderate' ? 'warning' : 'error'
                  }
                />
              </Box>
              {item.explanation && (
                <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                  {item.explanation}
                </Typography>
              )}
              <Typography variant='body2' color='primary' sx={{ fontWeight: 600 }}>
                {formatCurrency(item.cost_low)} – {formatCurrency(item.cost_high)}
              </Typography>
            </Box>
          ))}
        </Box>

        {report.overall_explanation && (
          <Typography variant='body2' color='text.secondary' sx={{ mt: 2, fontStyle: 'italic' }}>
            {report.overall_explanation}
          </Typography>
        )}
      </Box>
    )
  }

  const handlePrevious = () => {
    const newIndex = currentPhotoIndex > 0 ? currentPhotoIndex - 1 : photos.length - 1
    onPhotoChange(newIndex)
  }

  const handleNext = () => {
    const newIndex = currentPhotoIndex < photos.length - 1 ? currentPhotoIndex + 1 : 0
    onPhotoChange(newIndex)
  }

  const handleRotateLeft = () => {
    if (!currentPhoto || currentPhoto.mediaType === 'video') return
    const currentRotation = photoRotations[currentPhoto.id] || 0
    const newRotation = ((currentRotation - 90) % 360 + 360) % 360
    setPhotoRotations({
      ...photoRotations,
      [currentPhoto.id]: newRotation,
    })
  }

  const handleRotateRight = () => {
    if (!currentPhoto || currentPhoto.mediaType === 'video') return
    const currentRotation = photoRotations[currentPhoto.id] || 0
    const newRotation = ((currentRotation + 90) % 360 + 360) % 360
    setPhotoRotations({
      ...photoRotations,
      [currentPhoto.id]: newRotation,
    })
  }

  const getCurrentRotation = (): number => {
    if (!currentPhoto) return 0
    return photoRotations[currentPhoto.id] || 0
  }

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!open) return
      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        handlePrevious()
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        handleNext()
      } else if (event.key === 'Escape') {
        event.preventDefault()
        onClose()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, currentPhotoIndex, photos.length])

  if (!currentPhoto) return null

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='lg'
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          maxHeight: { xs: '100dvh', sm: '90vh' },
          minHeight: { xs: '100dvh', sm: '70vh' },
          position: 'relative',
          bgcolor: 'background.default',
        },
      }}
    >
      <DialogContent
        sx={{
          p: 0,
          height: { xs: 'auto', sm: '60vh' },
          flex: { xs: 1, sm: 'none' },
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 0,
          overflow: 'auto',
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.subtle',
            minHeight: 0,
            position: 'relative',
          }}
        >
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              py: 2,
              mb: 1,
              px: { xs: 2, sm: 0 },
            }}
          >
            <Tooltip title='Download this photo'>
              <Button
                onClick={handleDownload}
                startIcon={<DownloadIcon />}
                variant='outlined'
                color='primary'
                sx={{ fontWeight: 600, minWidth: 140 }}
              >
                DOWNLOAD
              </Button>
            </Tooltip>
            <Tooltip
              title={
                damageReport
                  ? showDamageReport
                    ? 'Close Damage Report'
                    : 'View Damage Report'
                  : 'Generate Damage Report'
              }
            >
              <Button
                onClick={
                  damageReport
                    ? () => setShowDamageReport(!showDamageReport)
                    : handleInspectDamage
                }
                startIcon={
                  damageReport ? showDamageReport ? <CloseIcon /> : <VisibilityIcon /> : <ReportProblemIcon />
                }
                variant={damageReport ? (showDamageReport ? 'outlined' : 'contained') : 'outlined'}
                color={damageReport ? (showDamageReport ? 'secondary' : 'warning') : 'primary'}
                disabled={isInspectingPhoto}
                sx={{ fontWeight: 600, minWidth: 200 }}
              >
                {isInspectingPhoto ? (
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                ) : damageReport
                  ? showDamageReport
                    ? 'CLOSE DAMAGE REPORT'
                    : 'VIEW DAMAGE REPORT'
                  : 'GENERATE DAMAGE REPORT'}
              </Button>
            </Tooltip>
          </Box>
          <IconButton
            onClick={handlePrevious}
            sx={{
              position: 'absolute',
              left: 12,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'grey.700',
              bgcolor: 'rgba(255,255,255,0.85)',
              '&:hover': { bgcolor: 'grey.200' },
              zIndex: 2,
              boxShadow: 1,
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <IconButton
            onClick={handleNext}
            sx={{
              position: 'absolute',
              right: 12,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'grey.700',
              bgcolor: 'rgba(255,255,255,0.85)',
              '&:hover': { bgcolor: 'grey.200' },
              zIndex: 2,
              boxShadow: 1,
            }}
          >
            <ArrowForwardIcon />
          </IconButton>
          {/* Rotation buttons - only show for photos, not videos */}
          {currentPhoto && currentPhoto.mediaType !== 'video' && (
            <>
              <Tooltip title="Rotate left (90°)">
                <IconButton
                  onClick={handleRotateLeft}
                  sx={{
                    position: 'absolute',
                    left: 12,
                    top: '20%',
                    transform: 'translateY(-50%)',
                    color: 'grey.700',
                    bgcolor: 'rgba(255,255,255,0.85)',
                    '&:hover': { bgcolor: 'grey.200' },
                    zIndex: 2,
                    boxShadow: 1,
                  }}
                >
                  <RotateLeftIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Rotate right (90°)">
                <IconButton
                  onClick={handleRotateRight}
                  sx={{
                    position: 'absolute',
                    right: 12,
                    top: '20%',
                    transform: 'translateY(-50%)',
                    color: 'grey.700',
                    bgcolor: 'rgba(255,255,255,0.85)',
                    '&:hover': { bgcolor: 'grey.200' },
                    zIndex: 2,
                    boxShadow: 1,
                  }}
                >
                  <RotateRightIcon />
                </IconButton>
              </Tooltip>
            </>
          )}
          <Box
            sx={{
              flex: 1,
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'background.subtle',
              position: 'relative',
              minHeight: 0,
              maxWidth: 600,
              maxHeight: 450,
              margin: '0 auto',
              borderRadius: 2,
              boxShadow: 1,
              overflow: 'visible',
            }}
          >
            {currentPhoto.mediaType === 'video' ? (
              <Box
                component="video"
                src={currentPhoto.url}
                controls
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  borderRadius: '4px',
                  display: 'block',
                  margin: '0 auto',
                }}
              />
            ) : (
              <ImageMagnifier
                key={currentPhoto.id}
                src={currentPhoto.url}
                alt={currentPhoto.angle}
                rotation={getCurrentRotation()}
                sx={{ width: '100%', height: '100%', maxHeight: 450 }}
                imgSx={{ maxHeight: 450, borderRadius: '4px' }}
              />
            )}
            {isInspectingPhoto && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  bgcolor: 'rgba(255, 255, 255, 0.95)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 2,
                }}
              >
                <CircularProgress size={60} sx={{ mb: 2, color: 'warning.main' }} />
                <Typography variant='h6' sx={{ fontWeight: 600, mb: 1, color: 'warning.main' }}>
                  AI is inspecting for damage...
                </Typography>
                <Typography variant='body2' color='text.secondary' sx={{ textAlign: 'center', maxWidth: 300 }}>
                  This may take a few seconds. The AI will analyze the photo for any visible damage.
                </Typography>
              </Box>
            )}
          </Box>
          <Box sx={{ p: 2, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Typography variant='h6' sx={{ fontWeight: 600, mb: 0.5 }}>
              {currentPhoto.angle}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              {currentPhoto.vehicleInfo}
            </Typography>
            {currentPhoto.uploadedAt && (
              <Typography variant='caption' color='text.secondary'>
                Uploaded: {formatTimestamp(currentPhoto.uploadedAt)}
              </Typography>
            )}
            <Chip
              label={`${currentPhotoIndex + 1} of ${photos.length}`}
              variant='outlined'
              size='small'
              sx={{ mt: 1 }}
            />
          </Box>
        </Box>
        {showDamageReport && currentPhoto && (damageReport || isLoadingDamageReport) && (
          <Paper
            sx={{
              width: { xs: 'auto', sm: 450 },
              p: 3,
              overflow: 'auto',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              borderTopRightRadius: { xs: 2, sm: 0 },
              borderBottomRightRadius: { xs: 2, sm: 0 },
              boxShadow: 2,
              mx: 2,
              mt: 2,
              mb: 2,
              bgcolor: 'background.paper',
              maxHeight: { xs: 'none', sm: 'calc(90vh - 64px)' },
              minWidth: { xs: 0, sm: 350 },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <ReportProblemIcon color='action' fontSize='small' />
              <Typography variant='h6' sx={{ fontWeight: 600 }}>
                Damage Report
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            {isLoadingDamageReport ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : damageReport ? (
              renderDamageReport(damageReport)
            ) : (
              <Alert severity='info'>No damage report available</Alert>
            )}
          </Paper>
        )}
      </DialogContent>
      <Divider sx={{ my: 0 }} />
      <DialogActions
        sx={{
          p: 2,
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          bgcolor: 'transparent',
        }}
      >
        <Button onClick={onClose} variant='contained' size='large'>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default PhotoViewerDialog
